import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { invalidateEntity, invalidateServerEntity } from '@/utils/queryInvalidation';
import { isDeployTerminal } from '../utils/deployStatus';
import { buildDeployWsUrl } from './wsUrl';

/**
 * Sincronización global (2026-09-27; ampliada 2026-10-09 con el canal `entities`: toda tabla se
 * refresca sola cuando el backend avisa que cambió, incluidos los procesos en background).
 * Sincronización de deploys (2026-09-27). Una única conexión WebSocket por sesión, suscripta
 * a los canales `deploys` y `approvals` SIN resourceId (el hub del backend entrega así los eventos
 * de TODOS los deploys). Cualquier evento — un deploy que avanza o termina, uno creado/cancelado
 * desde otra pestaña u otro usuario, una aprobación — invalida las vistas derivadas: tabla,
 * dashboard, aprobaciones, rollback y "último deploy" (del que depende el wizard para marcar la
 * versión ya desplegada). Sin esto, esas pantallas sólo se enteraban de cambios propios o cada
 * tanto por polling, y un usuario podía actuar sobre datos viejos.
 *
 * Los `deploy.update` llegan seguido mientras un deploy avanza: se agrupan (debounce) para no
 * refetchear en cada punto de progreso. El detalle de un deploy tiene su propio WebSocket; acá sólo
 * se lo invalida cuando ESE deploy llega a un estado terminal.
 */
export const LIVE_SYNC_DEBOUNCE_MS = 1000;
/** Los avisos `entity.changed` llegan en ráfagas (una acción toca varias entidades): se agrupan. */
export const ENTITY_SYNC_DEBOUNCE_MS = 250;
const MAX_RECONNECT_DELAY_MS = 30000;

type Envelope = {
  type?: string;
  channel?: string;
  resourceId?: string;
  data?: { deployId?: string; status?: string; entity?: string };
};

/** Lógica pura de "qué invalidar" — separada del socket para testearla. */
export function createLiveSyncHandler(
  queryClient: QueryClient,
  debounceMs = LIVE_SYNC_DEBOUNCE_MS,
  entityDebounceMs = ENTITY_SYNC_DEBOUNCE_MS,
) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let entityTimer: ReturnType<typeof setTimeout> | null = null;
  const finished = new Set<string>();
  const changedEntities = new Set<string>();

  const flushEntities = () => {
    entityTimer = null;
    changedEntities.forEach((entity) => void invalidateServerEntity(queryClient, entity));
    changedEntities.clear();
  };

  const flush = () => {
    timer = null;
    void invalidateEntity(queryClient, 'deploys', 'approvals', 'rollback', 'applications');
    finished.forEach((id) => {
      void queryClient.invalidateQueries({ queryKey: ['deploy', id] });
      void queryClient.invalidateQueries({ queryKey: ['deploy-logs', id] });
    });
    finished.clear();
  };

  const handle = (msg: Envelope) => {
    const type = msg.type ?? '';
    if (type === 'entity.changed') {
      // Cambió una tabla (por una acción, un proceso en background, otra pestaña u otro usuario):
      // se refresca la entidad afectada sin recargar la página.
      const entity = msg.data?.entity;
      if (entity) {
        changedEntities.add(entity);
        if (entityTimer) clearTimeout(entityTimer);
        entityTimer = setTimeout(flushEntities, entityDebounceMs);
      }
      return;
    }
    if (!type.startsWith('deploy.') && !type.startsWith('approval.')) return;
    if (type === 'deploy.log' || type === 'deploy.step') return; // detalle: lo maneja su propio socket
    const id = msg.data?.deployId ?? msg.resourceId;
    if (id && (type === 'deploy.completed' || isDeployTerminal(msg.data?.status))) finished.add(id);
    if (timer) clearTimeout(timer);
    timer = setTimeout(flush, debounceMs);
  };

  const dispose = () => {
    if (timer) clearTimeout(timer);
    if (entityTimer) clearTimeout(entityTimer);
    timer = null;
    entityTimer = null;
  };

  return { handle, dispose };
}

export function useDeploysLiveSync(enabled = true) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    const handler = createLiveSyncHandler(queryClient);
    let ws: WebSocket | null = null;
    let attempts = 0;
    let reconnect: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;
    let hasConnected = false;

    const connect = () => {
      if (stopped) return;
      try {
        ws = new WebSocket(buildDeployWsUrl());
      } catch {
        schedule();
        return;
      }
      ws.onopen = () => {
        attempts = 0;
        ws?.send(JSON.stringify({ type: 'subscribe', channel: 'deploys' }));
        ws?.send(JSON.stringify({ type: 'subscribe', channel: 'approvals' }));
        ws?.send(JSON.stringify({ type: 'subscribe', channel: 'entities' }));
        // Al (re)conectar se pudieron perder eventos: una invalidación trae todo al día. La primera
        // conexión sólo repasa deploys (las pantallas recién cargaron); una RE-conexión repasa todo.
        if (hasConnected) void queryClient.invalidateQueries();
        else void invalidateEntity(queryClient, 'deploys', 'approvals');
        hasConnected = true;
      };
      ws.onmessage = (event) => {
        try {
          handler.handle(JSON.parse(event.data as string) as Envelope);
        } catch {
          // mensaje no-JSON: se ignora
        }
      };
      ws.onclose = () => {
        ws = null;
        schedule();
      };
    };

    const schedule = () => {
      if (stopped) return;
      const delay = Math.min(1000 * 2 ** attempts, MAX_RECONNECT_DELAY_MS);
      attempts++;
      reconnect = setTimeout(connect, delay);
    };

    connect();
    return () => {
      stopped = true;
      handler.dispose();
      if (reconnect) clearTimeout(reconnect);
      if (ws) {
        ws.onclose = null;
        ws.close(1000, 'unmount');
      }
    };
  }, [enabled, queryClient]);
}
